<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Las fotos 360° borrosas están casi siempre causadas por uno de seis
            problemas técnicos específicos, cada uno con una solución clara.
            Entender qué causa está afectando tus imágenes te permite resolver
            el problema de forma eficiente, a menudo sin necesidad de volver a
            capturar.
          </p>
        </section>

        <section>
          <h2>Causa 1: Desenfoque por vibración (camera shake)</h2>
          <h3>Cómo identificarla</h3>
          <p>
            El desenfoque por vibración se manifiesta como un arrastre
            direccional en la imagen — los objetos parecen desplazarse en una
            dirección. Aparece principalmente en zonas poco iluminadas donde la
            cámara alarga automáticamente el tiempo de obturación.
          </p>
          <h3>Solución: trípode y velocidad de obturación</h3>
          <p>
            Solución principal: usa un trípode. La captura 360° a pulso en
            condiciones de poca luz produce desenfoque por vibración. Si no
            tienes trípode, bloquea la velocidad de obturación a un mínimo de
            1/60 s en modo Pro.
          </p>
        </section>

        <section>
          <h2>Causa 2: Desenfoque por sujetos en movimiento</h2>
          <p>
            Personas, animales u objetos que se mueven durante la captura crean
            fantasmas e inconsistencias en la imagen ensamblada.
          </p>
          <h3>Solución: esperar y recapturar</h3>
          <p>
            Espera a que las personas salgan del campo antes de capturar las
            panorámicas críticas. Para espacios públicos, captura temprano por
            la mañana o fuera del horario de apertura.
          </p>
        </section>

        <section>
          <h2>Causa 3: Errores de stitching (costuras visibles)</h2>
          <p>
            Las costuras visibles en la imagen ensamblada indican solapamiento
            insuficiente entre imágenes o movimiento demasiado rápido.
          </p>
          <h3>Solución: cubrir zonas faltantes</h3>
          <p>
            Usa el mapa de progreso de Travvir para verificar que todas las
            zonas estén correctamente cubiertas antes de terminar la captura.
            Recaptura las zonas marcadas como incompletas.
          </p>
        </section>

        <section>
          <h2>Causa 4: Mala exposición</h2>
          <p>
            Las imágenes sobreexpuestas (demasiado claras) o subexpuestas
            (demasiado oscuras) pierden detalle en las altas luces o las
            sombras.
          </p>
          <h3>Solución: bloqueo de la exposición</h3>
          <p>
            Bloquea la exposición antes de capturar. En iPhone, mantén pulsado
            hasta ver "AE/AF bloqueado". En Android, usa el modo Pro para fijar
            ISO y obturación. La IA de Travvir aplica también corrección
            automática en el procesamiento.
          </p>
        </section>

        <section>
          <h2>Causa 5: Nitidez insuficiente (enfoque incorrecto)</h2>
          <p>
            Si toda la imagen carece de nitidez sin desenfoque direccional, el
            enfoque automático probablemente se ha fijado en una zona
            incorrecta.
          </p>
          <h3>Solución: enfocar al infinito</h3>
          <p>
            Para espacios interiores de tamaño normal, ajusta manualmente el
            enfoque al infinito o sobre un objeto a 2–3 metros de distancia.
            Evita que el autofoco se fije en detalles muy cercanos.
          </p>
        </section>

        <section>
          <h2>Causa 6: Baja resolución de salida</h2>
          <p>
            Si tu foto 360° aparece globalmente suave y sin detalle, comprueba
            los parámetros de resolución de salida en los ajustes de la app.
          </p>
          <h3>Solución: maximizar la resolución</h3>
          <p>
            Travvir exporta automáticamente a la resolución máxima soportada por
            tu dispositivo. Asegúrate de no tener activado ningún modo de
            compresión para ahorrar espacio de almacenamiento.
          </p>
        </section>

        <section>
          <h2>Preguntas frecuentes</h2>

          <h3>
            ¿Por qué mis fotos 360° están borrosas solo en la parte inferior?
          </h3>
          <p>
            El desenfoque concentrado abajo indica falta de cobertura del suelo.
            Inclina el teléfono hacia abajo durante los últimos pasos de la
            captura para completar esa zona.
          </p>

          <h3>¿Puede la IA de Travvir corregir fotos ya borrosas?</h3>
          <p>
            Sí para desenfoques leves, gracias a la reducción de ruido y la
            nitidez automática. Los desenfoques severos requieren una nueva
            captura.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/tamano-imagen-tour-virtual">Tamaño y Resolución de Imágenes para Tours Virtuales 360° 2026</a></li>
          <li><a href="/blog/es/errores-fotografia-360">10 Errores de Fotografía 360° que Debes Evitar en 2026</a></li>
          <li><a href="/blog/es/fotos-360-con-celular">Cómo Tomar Fotos 360° con tu Celular (Sin Cámara Especial) 2026</a></li>
          <li><a href="/blog/es/fotografia-interior-360-tour-virtual">Fotografía de Interiores para Tours Virtuales 360°: Guía Técnica</a></li>
          <li><a href="/blog/es/mejor-app-camara-360-android">Mejor Aplicación Cámara 360° Android 2026: Comparativa Completa</a></li>
          <li><a href="/blog/es/mejor-app-camara-360-iphone">Mejor Aplicación Cámara 360° iPhone 2026: Comparativa Completa</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "corregir-fotos-360-borrosas",
);

useBlogSeo({
  title: "Fotos 360° Borrosas: 6 Causas y Soluciones Concretas 2026 | Travvir",
  description:
    "Diagnostica y corrige las fotos 360° borrosas: vibración, sujetos en movimiento, errores de stitching, mala exposición, enfoque incorrecto. Soluciones prácticas.",
  keywords:
    "fotos 360 borrosas solución, desenfoque foto 360, error stitching 360, calidad foto 360, mejorar foto 360 smartphone",
  slug: "corregir-fotos-360-borrosas",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question:
        "¿Por qué mis fotos 360° están borrosas solo en la parte inferior?",
      answer:
        "Indica falta de cobertura del suelo. Inclina el teléfono hacia abajo durante los últimos pasos de la captura.",
    },
    {
      question: "¿Puede la IA de Travvir corregir fotos ya borrosas?",
      answer:
        "Sí para desenfoques leves. Los desenfoques severos requieren una nueva captura.",
    },
  ],
});

</script>
